import { Component, inject, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WalletService } from 'src/app/services/wallet/wallet.service';
import { ToastrService } from 'ngx-toastr';
import { DateTime } from 'luxon';
import { UserService } from 'src/app/services/user/user.service';
import { finalize } from 'rxjs';
import { Wallet } from 'src/app/model/Wallet';
import { AchService, PendingPaymentData } from 'src/app/services/ach/ach.service';
import { TransactionDetail } from 'src/app/model/TransactionDetail';
import { NLSTransaction } from 'src/app/model/NLSTransaction';

/** Provided data to edit payment dialog */
export interface EditPaymentDialogData {
  transaction: NLSTransaction;
  payment: TransactionDetail;
}

/** Response data from edit payment dialog */
export interface EditPaymentDialogResponse {
  success: boolean;
  payment?: TransactionDetail;
}

/**
 * Dialog component for editing a payment
 * Allows modifying payment method, amount, and payment date
 */
@Component({
    selector: 'app-edit-payment-dialog',
    templateUrl: './edit-payment-dialog.component.html',
    styleUrls: ['./edit-payment-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditPaymentDialogComponent implements OnInit {
  /** Injected dependencies */
  public data: EditPaymentDialogData = inject(MAT_DIALOG_DATA);
  public dialogRef: MatDialogRef<EditPaymentDialogComponent> = inject(MatDialogRef<EditPaymentDialogComponent>);
  private _walletService: WalletService = inject(WalletService);
  private _achService: AchService = inject(AchService);
  private _toastr: ToastrService = inject(ToastrService);
  private _userService: UserService = inject(UserService);

  /** Available wallets for payment method selection */
  public wallets: Wallet[] = [];

  /** Display data */
  public today: Date = DateTime.now().startOf('day').toJSDate();
  public fullName: string = '';

  /** Form data */
  public selectedWallet: Wallet | null = null;
  public formPayment!: TransactionDetail;

  /** Submission state flag */
  public isSubmitting: boolean = false;

  /**
   * Initialize the component when the dialog is opened
   */
  async ngOnInit(): Promise<void> {
    // Subscribe to wallets in case initial store is empty
    const sub = this._walletService.wallets$.subscribe((wallets: Wallet[]) => {
      if (wallets != undefined) {
        this.wallets = wallets;
        this.selectedWallet = this.wallets.length > 0 ? this.wallets[0] : null;
        sub.unsubscribe();
      }
    });

    // Set default wallet
    const wallets = this._walletService.getWallets();
    if (wallets != undefined) {
      this.wallets = wallets;
      this.selectedWallet = wallets.length > 0 ? wallets[0] : null;
      sub.unsubscribe();
    }

    // Set full name from user service
    this.fullName = `${this._userService.user?.firstName} ${this._userService.user?.lastName}`;

    // Ensure we have payment data to edit (should be guaranteed to exist)
    if (this.data.payment == undefined) {
      throw new Error('No existing payment data provided to edit payment dialog');
    }
    this.formPayment = this.data.payment.copyOf();

    // NOTE: I don't think we're storing which wallet was used for the payment anywhere, so we can't pre-select it
  }

  /**
   * Submit the edited payment
   */
  public onSubmitEdit(): void {
    try {
      // Check for required data
      if (!this.data.transaction || !this.data.payment) {
        throw new Error('No existing pending payment to update');
      }

      if (!this.selectedWallet) {
        throw new Error("No wallet selected for payment method");
      }

      this.isSubmitting = true;

      // Construct payment data to update to
      const paymentData: PendingPaymentData = {
        transactionId: parseInt(this.data.payment.transaction_reference_no),
        loanNumber: this.data.payment.loan_number,
        walletId: this.selectedWallet.achwalletID,
        amount: this.formPayment.transaction_amount,
        paymentDate: this.formPayment.effective_date ? new Date(this.formPayment.effective_date) : null,
      };

      // Update pending payment via ACH service
      this._achService.updatePendingPayment$(paymentData.loanNumber, this.data.payment.transaction_reference_no, paymentData).pipe(
        // On update finish, set submitting to false after short delay (after dialog close)
        finalize(() => setTimeout(() => (this.isSubmitting = false), 1000))
      ).subscribe({
        // On success, show toast and close dialog with success
        next: (_: any) => {
          this._toastr.success('Payment updated successfully!');

          const resp: EditPaymentDialogResponse = {
            success: true,
            payment: this.formPayment,
          }

          this.dialogRef.close(resp);
        },
        // Catch http errors
        error: (err: any) => {
          console.error('[EDIT-PAYMENT-DIALOG]: error updating payment', err);
          this._toastr.error('Payment update failed');
        },
      });
    } catch (err: any) {
      // Catch thrown errors
      console.error('[EDIT-PAYMENT-DIALOG]: exception updating payment', err);
      this._toastr.error(err?.message || 'Payment update failed');
      this.isSubmitting = false;
    }
  }

  /**
   * Close the dialog without saving
   */
  public cancel(): void {
    this.dialogRef.close({ success: false } as EditPaymentDialogResponse);
  }
}
